import type { CombatantCurrent, CombatantId } from "./combatant";
import type { CombatantBase } from "./combatantBase";
import { weaponInstanceKey } from "./weapons";
import type { ActivationState } from "./turn";

/**
 * The **action economy** — the per-turn budget, and the namespaced keys and readers for the
 * sparse `current.resources` ledger that carries every usage count.
 *
 * Two disciplines hold across everything here:
 *
 * - **One door per key.** Each namespaced resource key has exactly one key-builder and one
 *   reader; no call site may derive or peek at a raw key by hand. That is what keeps the
 *   resolve-path gate and the frontend chooser-greying predicate from drifting apart.
 * - **Consumed-count polarity, absent = 0 = full.** Every counter here counts *up* from
 *   absent, so a fresh combatant needs nothing seeded. The one exception is Loading's
 *   `loadedResourceKey` (absent = loaded), which is a binary state rather than a count and
 *   lives with the weapon profile.
 *
 * The cadences form a set that `@lancer-vtt/rules`' `checkActionAvailable` unifies at one
 * read predicate: turn → {@link canUseAction}, round → {@link canUseRoundAction},
 * scene/Limited → {@link canUseSceneAction}. All three take an already-resolved allowance,
 * so each stays a pure, dependency-free read.
 */

/**
 * The **per-turn action/move budget**. Lives *inside* the `acting` arm of {@link Turn}, so it
 * exists iff a unit is mid-turn and belongs to that unit. Reactions are **not** here — they
 * outlive a turn, and the global slot is refreshed BY a turn starting rather than reset when
 * one ends, so they sit on {@link ActivationState}. JSON-shaped (no class) — travels the wire
 * and persists.
 */
export type TurnBudget = {
  /**
   * Action **points** the active combatant has left this turn. Reset to the combatant's
   * allotment (2 for a mech — {@link ACTIONS_PER_TURN}) by `activate`. A Quick action costs
   * 1, a Full action costs 2; free and protocol actions cost 0.
   */
  actions: number;
  /**
   * **Move points** the active combatant has left this turn — the move analogue of
   * `actions`. Reset to the active combatant's **Speed** (`base.speed`) by `activate`,
   * decremented by every `move_token` that combatant makes (by the path's cost), and the
   * wall a further move is validated against — so one Speed-worth of movement splits freely
   * across multiple moves around other actions, and the unit can't out-move its Speed by
   * re-issuing from each new cell. Only the acting unit has a move budget; a non-acting unit
   * can't PLAY-move at all, so there is no untracked full-Speed fallback. First placement (a
   * `pos == null` deploy) is budget-exempt.
   */
  move: number;
  /**
   * The **once-per-turn action-use ledger** — how many times the active combatant has used
   * each RAW-catalog action id (`skirmish`, `boost`, `ram`, `barrage`, `stabilize`,
   * `overcharge`, `quick_tech`, `full_tech`, …) **this turn**. Reset to `{}` by `activate`
   * alongside `actions`/`move`, so the ledger resets every turn for free. Legality reads
   * {@link canUseAction}: `(actionUses[id] ?? 0) < allowance`, the allowance defaulting to 1
   * (RAW "once per turn") unless the combatant's frozen
   * {@link CombatantBase.actionAllowances} overrides it. Absent ⇒ every id reads as unused.
   *
   * Overcharge rides this generic ledger (`actionUses["overcharge"]`) rather than a parallel
   * single-purpose flag.
   */
  actionUses?: Record<string, number>;
  /**
   * The **protocol-safe per-turn action-use ledger** — a SIBLING of {@link actionUses}, same
   * shape and default-1 allowance (it reads through {@link canUseProtocolAction}), but
   * reserved for a granted action's Protocol-activation `1/turn` cap.
   *
   * **Kept deliberately OUT of `actionUses`** — originally because the turn-virgin window read
   * `actionUses` being empty as "no action taken yet," so a Protocol's per-turn cap folded in
   * there would have dirtied that window and wrongly closed it (`decisions/0072` pt 4).
   * **That rationale is SUPERSEDED**: {@link turnBudgetVirgin} is cost-keyed and no longer
   * reads `actionUses` at all, so the separation no longer protects the window. The behavior
   * stays correct and the sibling stays where it is — merging the two back carries its own
   * message/gating risk and is a named deferral in `projects/action-ordering-audit.md` D4, not
   * an accident. What the separation still buys: a Protocol never appears in the
   * once-per-turn ledger the action bar and `checkActionUseLegality` read.
   *
   * Reset to `{}` by `activate` alongside the rest — required so a multi-activation
   * combatant's second activation in a round reopens a fresh turn-virgin window even after
   * its first activation ledgered a protocol.
   */
  protocolUses?: Record<string, number>;
  /**
   * The **spend counter** — how many action points and move points this combatant has
   * actually *spent* so far this turn, counting UP from 0. Reset to `{ actions: 0, move: 0 }`
   * by `activate` alongside `actionUses`/`protocolUses`. **Optional, absent ⇒ both 0**, read
   * through {@link spentActions}/{@link spentMove} — `TurnBudget` is a hot, wire-travelling,
   * persisted struct, so the field must be omittable and every reader must tolerate its
   * absence. `migrate.ts`'s `coerceTurn` synthesizes it for a blob written before the field
   * existed.
   *
   * **Why a counter and not a test over what REMAINS.** The "must be done first" window
   * (Ordnance's firing window, Core p.105; Protocol start-of-turn timing, p.62) needs to know
   * whether anything has been *done* yet. Every remaining-budget test fails, because a grant
   * absorbs a spend: Everest's Initiative grants +1 Quick (`actions: 3`), a following Skirmish
   * takes it back to `2`, and the result is byte-identical to a pristine turn under `===` OR
   * `>=`. Same for Overcharge-then-Skirmish. Only counting what went out separates
   * "granted, then spent" from "untouched". See `projects/action-ordering-audit.md` D2.
   *
   * **Cost-keyed, per that tracker's D1 (a developer ruling).** An activation increments this
   * iff it actually charged action points or move points. A genuinely free activation — a
   * Protocol, an earmarked free Boost, Overcharge (which costs heat, not an action) — spends
   * nothing and so leaves the counter alone. Grants (`grant_move`, `grant_overcharge`,
   * `grant_action_allowance`) *raise* the remaining budget and are likewise not spends.
   *
   * Every inverse is free: each command that writes this snapshots the whole
   * {@link ActivationState} into its `restore_budget`/`restore_pos`/`restore_stand` inverse,
   * so the counter round-trips with everything else. **If any of those inverses ever narrows
   * to a delta, this field needs its own inverse** — pinned by a round-trip test in
   * `reducer.test.ts`.
   */
  spent?: { actions: number; move: number };
}

/** Action points spent so far this turn; `spent` absent ⇒ 0. The one door onto that read. */
export function spentActions(budget: TurnBudget): number {
  return budget.spent?.actions ?? 0;
}

/** Move points spent so far this turn; `spent` absent ⇒ 0. The one door onto that read. */
export function spentMove(budget: TurnBudget): number {
  return budget.spent?.move ?? 0;
}

/**
 * Fold a spend into {@link TurnBudget.spent} — the single writer every decrement site goes
 * through, so the counter can't drift one site at a time. Returns the new `spent` record
 * (never mutates); an absent prior counter reads as 0/0, so this is total over any budget.
 *
 * `delta` is what was **charged**, which is deliberately not always what the accompanying
 * subtraction removed: `spend_action` floors its subtraction at 0 for replay totality, and an
 * over-spend against an empty budget must still read as a spend rather than as nothing done.
 */
export function recordSpend(
  budget: TurnBudget,
  delta: { actions?: number; move?: number },
): { actions: number; move: number } {
  return {
    actions: spentActions(budget) + (delta.actions ?? 0),
    move: spentMove(budget) + (delta.move ?? 0),
  };
}

/**
 * **The "must be done first" window — the ONE definition, shared by every consumer.** Is
 * `budget`'s turn still *virgin*: has its combatant spent **nothing at all** yet this turn?
 *
 * Backs three RAW clauses that are the same question asked three ways:
 *   - **Ordnance's firing window** (Core p.105 — "can only be fired before the user moves or
 *     takes any other actions on their turn, excepting PROTOCOLS"), via
 *     `@lancer-vtt/rules`' `isTurnVirgin`/`checkOrdnanceWindow`;
 *   - **Protocol start-of-turn timing** (p.62 — "at the start of their turn, before moving or
 *     taking any other action"), via `checkGrantedActionLegality`'s Protocol leg;
 *   - **the same-Size grapple tiebreak's "at the start of their turn"** (p.69), via
 *     `checkGrappleContestLegality`.
 *
 * **COST-KEYED, per `projects/action-ordering-audit.md` D1 (a developer ruling).** The window
 * closes iff action points or move points were actually *spent*. A genuinely free activation —
 * a Protocol, Overcharge (which costs heat, not an action), an earmarked free Boost, a free
 * grapple release — spends nothing and leaves it open. So does a *grant*: Everest's Initiative
 * hands out a +1 Quick allowance and its Hyperspec rider a free Boost, and neither is an action
 * in RAW. The genuine-free-action half is a **knowing divergence** from the strictest reading of
 * p.105's "excepting PROTOCOLS"; D1 records the price.
 *
 * **Why it reads {@link TurnBudget.spent} and NOT the remaining budget or
 * {@link TurnBudget.actionUses}** — both of the predicates this replaced read those, and both
 * mis-fired:
 *   - *Remaining budget* (`actions === ACTIONS_PER_TURN && move === base.speed`) cannot work at
 *     all: a grant ABSORBS a spend. Initiative's +1 Quick makes `actions: 3`; a following
 *     Skirmish returns it to `2`, byte-identical to a pristine turn under `===` or `>=`. See
 *     {@link TurnBudget.spent}'s own doc.
 *   - *`actionUses` being empty* mis-fires the other way: **every** non-Protocol granted
 *     `request_activate` stamps that ledger, zero-cost grant-only rows included, so pressing
 *     Initiative closed a window nothing had been spent into (D4). `actionUses` is deliberately
 *     left untouched — the once-per-turn wall still reads it; this window simply stopped.
 *
 * **PURE over `TurnBudget`** — no `EncounterState`, no `base.speed` lookup, no imports. That is
 * what lets this be the single copy: it lives in `@lancer-vtt/domain` beside
 * {@link canUseAction}, under that function's own rationale, because two consumers
 * (`handlers/turns.ts`'s Flying-grounding check, `handlers/placement.ts`'s `stand_up`) are
 * **domain-side and cannot import from `@lancer-vtt/rules` at all**. The rules-side wrappers add
 * only the turn-phase/actor short-circuit they each need. `grapple.ts` used to carry a
 * hand-copied leaf duplicate of the old body to dodge an import cycle; purity removed the
 * reason, so the duplicate was deleted rather than pinned with a parity test.
 *
 * **The blind spot to know about:** a **reaction taken on one's own turn** spends the global
 * `reactionSlot`/`reactionUses`, never this budget, so it would leave the window open. Nothing
 * reaches it today (Overwatch/Brace only fire on someone ELSE's turn); if a same-turn reaction
 * is ever built, this predicate must learn about it explicitly.
 */
export function turnBudgetVirgin(budget: TurnBudget): boolean {
  return spentActions(budget) === 0 && spentMove(budget) === 0;
}

/**
 * May `id` still be used this turn, given `budget` and an optional resolved `allowance`?
 * Defaults to **1** (RAW: once per turn) — the caller resolves any override off the acting
 * combatant's frozen `CombatantBase.actionAllowances[id]` before calling in, so this helper
 * stays a pure predicate over `TurnBudget`. Reads `budget.actionUses[id]`, absent ⇒ 0 uses.
 *
 * Lives in `@lancer-vtt/domain` (not `@lancer-vtt/rules`) so it can be shared by BOTH call
 * sites without violating the standing rule that `domain` never depends on `rules`: the
 * domain reducer's `boost` case calls it directly (Boost self-enforces inside the pure
 * reducer, unlike every other action, which is gated by the room before it folds a command),
 * and `@lancer-vtt/rules`' `economy.ts` calls the SAME function for everything else. Don't
 * move this without re-checking Boost's call site.
 */
export function canUseAction(budget: TurnBudget, id: string, allowance = 1): boolean {
  return (budget.actionUses?.[id] ?? 0) < allowance;
}

/**
 * The **per-option** once-per-turn `actionUses` slot for one Quick Tech option
 * (`act_scan`/`act_lock_on`/`act_bolster`/`act_invade`, or a system-granted tech attack's key).
 * RAW Core p.69: "Unlike other quick actions, QUICK TECH can be taken more than once per turn;
 * however, a different option must be chosen every time." So `"quick_tech"` names the action and
 * holds its allowance, while the repeat wall counts this slot. Lives here so the domain `prepare`
 * reducer (a readied Invade) and `@lancer-vtt/rules`' `quickTechOptionLedgerId` (live uses) stamp
 * and count the SAME key.
 */
export function quickTechOptionKey(option: string): string {
  return `quick_tech:${option}`;
}

/**
 * May `id` still be used this turn on the **protocol-safe** per-turn ledger — the sibling of
 * {@link canUseAction} that reads {@link TurnBudget.protocolUses}, so a granted Protocol's
 * own `1/turn` cap never ledgers into `actionUses`, which Ordnance's firing window reads
 * being empty as "no action taken yet this turn." Same default-1, absent-⇒-unused shape; the
 * caller resolves any override off the frozen affordance's `cap.max` before calling in.
 */
export function canUseProtocolAction(budget: TurnBudget, id: string, allowance = 1): boolean {
  return (budget.protocolUses?.[id] ?? 0) < allowance;
}

/** A mech's per-turn action-point allotment (2: two Quick, or one Full). */
export const ACTIONS_PER_TURN = 2;

/**
 * The combatant-resource key carrying a mech's **Overcharge escalation step** — the count of
 * times it has Overcharged since its last full repair, i.e. the index into the
 * 1 → 1d3 → 1d6 → 1d6+4 self-heat ladder (Core p.78). Absent reads as step 0, so a freshly
 * deployed or full-repaired mech Overcharges for 1. A full repair (a downtime re-deploy)
 * re-seeds it to 0; an Overcharge advances it by one. Encounter-scoped.
 */
export const OVERCHARGE_COUNT_RESOURCE = "overcharge_count";

/**
 * The combatant-resource key carrying the count of attacks a combatant has **DECLARED so far
 * this turn** — what backs "the first melee attack you make … on your turn" style clauses.
 * Absent reads as 0.
 *
 * **Reset and increment both ride existing fired trigger points**, not a bespoke state
 * machine: `resolveAttackOrdinalReset` zeroes this at the fired `turn_start`, and
 * `fireAttackDeclared` increments it by 1 at the fired `on_attack_declared` choke point (once
 * per declared attack — a Blast fan-out catching N targets still declares once). Because both
 * mutations ride real, ordered log entries at the SAME points the trigger bus already fires,
 * the sequence reproduces identically on replay.
 *
 * {@link attacksDeclaredThisTurn} is the one reader; the `first_attack_this_turn` predicate
 * is its one consumer, testing whether the count was 0 at the ATTACKER's declaration time
 * (read before that same attack's own increment lands, since state doesn't mutate
 * mid-resolution).
 */
export const ATTACK_ORDINAL_RESOURCE = "attack_ordinal_this_turn";

/**
 * How many attacks has this combatant declared so far this turn? Reads
 * {@link ATTACK_ORDINAL_RESOURCE}; absent reads as 0. The one door onto the raw key.
 */
export function attacksDeclaredThisTurn(resources: Record<string, number> | undefined): number {
  return resources?.[ATTACK_ORDINAL_RESOURCE] ?? 0;
}

/**
 * The combatant-resource key: has this mover already dealt its Trample sweep damage **THIS
 * TURN** (RAW Core p.297, "1/turn dealing … damage")? Absent reads as not-yet-swept.
 *
 * **Deliberately a dedicated, single-key, TURN-scoped counter — the {@link
 * ATTACK_ORDINAL_RESOURCE} shape, NOT the generic {@link RESOURCE_CAP_PREFIX}
 * `modifier_consume_resource_on_apply` "1/round" convention.** The generic cap resets at
 * `round_start` (`resolveResourceCapReset`), which is wrong here: `npct_elite`'s Ready and
 * Waiting grants a carrier **two turns in one round** (this roster's own boss, the Prince,
 * ships with `extraActivations: 1`), and RAW's "1/turn" must reset with EACH of those turns,
 * not once a round. A round-scoped cap would silently deny the Prince's second activation its
 * own trample sweep. Reset by `resolveTrampleSweepReset` (`resolve/resets.ts`) at the SAME
 * `turn_start` choke point {@link ATTACK_ORDINAL_RESOURCE}'s own reset rides.
 *
 * **Why an engine-side counter and not the content clause's own `consumeResourceOnApply`
 * cap:** that cap consumes per INDIVIDUAL `fireTrigger` call, but one qualifying move can
 * cross several characters in a single sweep and RAW wants every one of THEM hit once, with
 * only the SWEEP AS A WHOLE capped at once per turn — consuming on the first crossed
 * character's own fire would starve the second crossed character in the very same sweep.
 * `referee/moveFollowups.ts`'s `trampleInto` checks this counter ONCE, before firing any
 * per-crossed-character trigger, and increments it ONCE for the whole sweep, never per target.
 */
export const TRAMPLE_SWEEP_RESOURCE = "trample_swept_this_turn";

/** Has `resources`' carrier already dealt its Trample sweep damage this turn? See {@link TRAMPLE_SWEEP_RESOURCE}. */
export function hasSweptTrampleThisTurn(resources: Record<string, number> | undefined): boolean {
  return (resources?.[TRAMPLE_SWEEP_RESOURCE] ?? 0) > 0;
}

/** Which way an {@link import("./weapons").WeaponProfile.eitherMode} weapon is being used. */
export type WeaponMode = "melee" | "ranged";

/**
 * The combatant-resource key prefix for the **"ranged or melee, not both in the same turn"
 * latch** on an {@link import("./weapons").WeaponProfile.eitherMode} weapon (the Ram Cannon,
 * Core p.297). One key per weapon instance: `weapon_mode:<weaponInstanceKey>`.
 *
 * **The value names the turn as well as the mode**, so the latch needs no reset: it is
 * `(turnOrdinal + 1) * 2 + (ranged ? 1 : 0)` ({@link encodeWeaponModeLatch}), and a value
 * stamped on an earlier turn simply reads as no latch ({@link latchedWeaponMode}). Keying on the
 * global `ActivationState.turnOrdinal` rather than a `turn_start` reset makes "the same turn"
 * mean the turn in progress, whoever's it is. An Elite's two activations are two turns, and an
 * Overwatch fired on a PC's turn latches that turn only. The trample-sweep counter needed a
 * per-carrier reset for the same Elite; this key does not.
 *
 * Written as a `resource_delta` (the latest value minus the stored one, always positive because
 * `turnOrdinal` only grows), so undo is the plain inverse and the narrator stays quiet, as it
 * does for every other bookkeeping `resource_delta`.
 */
export const WEAPON_MODE_LATCH_PREFIX = "weapon_mode:";

/** The latch key for one weapon instance. See {@link WEAPON_MODE_LATCH_PREFIX}. */
export function weaponModeLatchKey(instanceKey: string): string {
  return `${WEAPON_MODE_LATCH_PREFIX}${instanceKey}`;
}

/** The stored latch value for `mode` fired on turn `turnOrdinal`. See {@link WEAPON_MODE_LATCH_PREFIX}. */
export function encodeWeaponModeLatch(turnOrdinal: number, mode: WeaponMode): number {
  return (turnOrdinal + 1) * 2 + (mode === "ranged" ? 1 : 0);
}

/**
 * The mode `instanceKey` was already used in THIS turn, or `undefined` when it has not been
 * used yet this turn (no key, or a key stamped on an earlier turn).
 */
export function latchedWeaponMode(args: {
  resources: Record<string, number> | undefined;
  instanceKey: string;
  turnOrdinal: number;
}): WeaponMode | undefined {
  const { resources, instanceKey, turnOrdinal } = args;
  const v = resources?.[weaponModeLatchKey(instanceKey)];
  if (v === undefined || Math.floor(v / 2) - 1 !== turnOrdinal) return undefined;
  return v % 2 === 1 ? "ranged" : "melee";
}

/**
 * The NAMESPACE PREFIX every `modifier_consume_resource_on_apply` usage counter is stored
 * under — the shared "1/round" frequency-cap primitive.
 *
 * **Why a scannable prefix rather than one known key:** an unbounded number of distinct
 * capped clauses can coexist on one combatant (two different capped talents, say), so the
 * reset can't zero a single key. Namespacing every capped clause under this prefix (via
 * {@link resourceCapKey}) lets `resolveResourceCapReset` sweep every capped counter off a
 * combatant's `current.resources` in one pass, without knowing in advance which clauses that
 * combatant carries.
 */
export const RESOURCE_CAP_PREFIX = "consume_cap:";

/**
 * The `current.resources` key carrying `rawKey`'s per-cadence usage count (see
 * {@link RESOURCE_CAP_PREFIX}). The one door onto the raw namespaced key.
 */
export function resourceCapKey(rawKey: string): string {
  return `${RESOURCE_CAP_PREFIX}${rawKey}`;
}

/**
 * Has `rawKey`'s per-cadence usage cap room for one more application? Absent reads as 0
 * consumed — a pristine cadence, nothing spent yet.
 */
export function resourceCapAvailable(resources: Record<string, number> | undefined, rawKey: string, max: number): boolean {
  return (resources?.[resourceCapKey(rawKey)] ?? 0) < max;
}

/**
 * How often a `consumeResourceOnApply` cap refills: `"round"` (the default) at `round_start`,
 * `"own_turn"` at the start of the carrier's own turn — RAW's "the first … on your turn", where
 * the cap is the per-turn ledger that makes "first" count only the attacks the clause matches —
 * and `"turn"` at the start of every turn, anyone's: RAW's "1/turn", so a use on your own turn
 * leaves an Overwatch on the next unit's turn its own use. `"scene"` never refills inside the
 * encounter (RAW "1/scene"): no turn or round sweep takes it, and it goes with the encounter.
 */
export type ResourceCapCadence = "round" | "own_turn" | "turn" | "scene";

/**
 * The raw-key tag an `"own_turn"` cap's counter carries, so the carrier's `turn_start` reset can
 * find it: its stored key is `consume_cap:own_turn:<key>` ({@link ownTurnCapPrefix}). The
 * `round_start` sweep takes these too, which is harmless: a round boundary always falls between
 * two of a carrier's turns.
 */
const OWN_TURN_CAP_TAG = "own_turn:";
const TURN_CAP_TAG = "turn:";
const SCENE_CAP_TAG = "scene:";

/** The stored-key prefix of every `"own_turn"` cap counter. See {@link ResourceCapCadence}. */
export function ownTurnCapPrefix(): string {
  return resourceCapKey(OWN_TURN_CAP_TAG);
}

/** The stored-key prefix of every `"turn"` cap counter, swept at every turn start. See {@link ResourceCapCadence}. */
export function turnCapPrefix(): string {
  return resourceCapKey(TURN_CAP_TAG);
}

/** The stored-key prefix of every `"scene"` cap counter, which the round sweep leaves alone. See {@link ResourceCapCadence}. */
export function sceneCapPrefix(): string {
  return resourceCapKey(SCENE_CAP_TAG);
}

/**
 * The raw key a cap's counter lives under (feed it to {@link resourceCapKey} /
 * {@link resourceCapAvailable}): the authored `key`, tagged by its cadence when it is not the
 * round's. A per-target cap ("1/scene on each character") passes the target, and each target
 * gets a counter of its own on the carrier: `scene:<key>@<targetId>`.
 */
export function resourceCapRawKey(cap: { key: string; per?: ResourceCapCadence }, targetId?: string): string {
  const key = targetId === undefined ? cap.key : `${cap.key}@${targetId}`;
  if (cap.per === "own_turn") return `${OWN_TURN_CAP_TAG}${key}`;
  if (cap.per === "scene") return `${SCENE_CAP_TAG}${key}`;
  return cap.per === "turn" ? `${TURN_CAP_TAG}${key}` : key;
}

/**
 * The NAMESPACE PREFIX a `grant_action_allowance` atom's bonus-use grant is stored under —
 * the **runtime** sibling of {@link CombatantBase.actionAllowances}.
 *
 * That frozen map is spawn-time data, so a mid-combat triggered grant (an on-hit "you can
 * make another melee attack with the same weapon as a free action", authored as a bonus
 * `skirmish` use) can't populate it. This is the `current.resources`-backed, per-action-id
 * bonus count instead: additive on TOP of whatever `actionAllowances`/the frozen affordance's
 * `cap` already resolves, never replacing it. Mirrors {@link RESOURCE_CAP_PREFIX}'s shape — an
 * unbounded number of distinct action ids can each carry their own grant, so the reset
 * sweeps by prefix rather than zeroing one known key.
 *
 * **Turn-scoped, not encounter-durable** (unlike the scene bag): a granted allowance is
 * earned "immediately" and is reset for the CARRYING combatant at its own next `turn_start`
 * — the same choke point {@link ATTACK_ORDINAL_RESOURCE} resets at, not a cross-combatant
 * round sweep. An unused grant does not carry forward.
 */
export const GRANTED_ACTION_ALLOWANCE_PREFIX = "granted_allowance:";

/**
 * The `current.resources` key carrying `actionId`'s runtime-granted bonus-use count (see
 * {@link GRANTED_ACTION_ALLOWANCE_PREFIX}). The one door onto the raw namespaced key.
 * `actionId` is the same id space `canUseAction` keys on — a RAW-catalog verb (`skirmish`,
 * `barrage`, …) or a granted action's composite `<itemId>:<actionIndex>` id.
 */
export function grantedActionAllowanceKey(actionId: string): string {
  return `${GRANTED_ACTION_ALLOWANCE_PREFIX}${actionId}`;
}

/**
 * How many bonus uses of `actionId` has a combatant been granted this turn? Absent reads as
 * 0. The caller ADDS this to whatever static allowance it already resolved
 * (`base.actionAllowances`/the frozen affordance's `cap.max`, or the RAW default of 1) before calling
 * {@link canUseAction} — this reader never gates by itself.
 */
export function grantedActionAllowanceBonus(resources: Record<string, number> | undefined, actionId: string): number {
  return resources?.[grantedActionAllowanceKey(actionId)] ?? 0;
}

/**
 * The closed set of **RAW verb names** a `grant_free_action_use` earmark may name.
 *
 * **This is a THIRD id space, deliberately distinct from the two it sits between:**
 *
 * - It is NOT the once-per-turn **ledger id** space `actionIdForIntent` derives. That space
 *   COLLAPSES Scan/Lock On/Bolster into a single `"quick_tech"`, so an earmark keyed on it
 *   would let a talent that grants a free **Lock On** pay for a free **Invade** instead — the
 *   same over-grant this primitive exists to remove, one level down.
 * - It is NOT the composite carrier id space (`"ms_markerlight:0"`) either.
 *
 * **Boost is the one verb where the earmark name and the ledger id coincide**, which is
 * exactly why a single-carrier implementation hides the divergence — it would ship the Lock
 * On bug with every test green. The divergence is pinned by `matchesEarmarkedVerb`'s own
 * tests rather than left to a carrier to reveal.
 *
 * Authors write RAW words like every other authored field, and the schema enum makes a typo a
 * parse error rather than a silently-dead earmark. `@lancer-vtt/rules`' `matchesEarmarkedVerb`
 * is the ONE door from a verb to "is this intent that verb" — nothing else may re-derive it.
 *
 * See also {@link ATTACK_VERBS} just below: the **attack-verb space**, a SEPARATE and
 * deliberately un-merged vocabulary. The two share members (`skirmish`, `ram`, `grapple`) and
 * sit in the same file, and they must still not be unified; see that constant's doc.
 */
export const EARMARK_VERBS = [
  "boost",
  "skirmish",
  "ram",
  "grapple",
  "stabilize",
  "lock_on",
  "scan",
  "bolster",
  "mount",
  "dismount",
] as const;

/** One of {@link EARMARK_VERBS} — the RAW verb an earmark names. */
export type EarmarkVerb = (typeof EARMARK_VERBS)[number];

/** Is `v` a known RAW earmark verb? The runtime narrowing behind the schema enum. */
export function isEarmarkVerb(v: string): v is EarmarkVerb {
  return (EARMARK_VERBS as readonly string[]).includes(v);
}

/**
 * The closed **attack-verb space**: every id `@lancer-vtt/rules`' `actionIdForAttackLike` can
 * derive for an in-flight attack, and the ONLY values `AttackMatch.verb`,
 * `ModifierContribution.attackScope.verb` and {@link RulingSpec.verb} are compared against.
 * Mostly RAW verb names, with two collapses an author must know about:
 *
 * - **Invade presents as `"quick_tech"`** (the ledger id), and a system-granted tech attack as
 *   its frozen activation (`"quick_tech"` or `"full_tech"`).
 * - **A Barrage shot presents as `"skirmish"`.** `refereeIntent.ts` synthesizes each shot as a
 *   plain `RequestAttack`, so nothing downstream can tell it from a Skirmish: "on a Barrage" is
 *   inexpressible, and "on a Skirmish" also fires on every Barrage shot. `"barrage"` is NOT a
 *   member, although it is a live id in the action-allowance space
 *   (`grantedActionAllowanceKey`).
 *
 * **Not {@link EARMARK_VERBS}, and must not be merged with it.** The earmark space is
 * pre-collapse (`lock_on`/`scan`/`bolster` stay distinct so a free Lock On cannot pay for an
 * Invade) and holds non-attack verbs; this one is the attack derivation's output, collapsed
 * where it collapses. One shared list would either let an earmark name `quick_tech` (the
 * over-grant) or let an attack scope name `boost` (a scope nothing can match).
 *
 * Closed rather than a free string so an authored verb that no attack can produce (a typo, or
 * `"barrage"`) fails the pack schema instead of parsing clean and silently matching nothing.
 * `actionIdForAttackLike`'s inferred return type is pinned equal to this list at compile time
 * (`actionId.ts`), so a new or removed arm there is a tsc error until this list follows.
 */
export const ATTACK_VERBS = ["ram", "grapple", "improvised_attack", "quick_tech", "full_tech", "fight", "skirmish"] as const;

/** One of {@link ATTACK_VERBS}: the verb an attack presents as. */
export type AttackVerb = (typeof ATTACK_VERBS)[number];

/** Is `v` a known attack verb? The runtime narrowing behind the schema enum and `lvtt ruling --verb`. */
export function isAttackVerb(v: string): v is AttackVerb {
  return (ATTACK_VERBS as readonly string[]).includes(v);
}

/**
 * The NAMESPACE PREFIX a `grant_free_action_use` earmark is stored under.
 *
 * **How this differs from {@link GRANTED_ACTION_ALLOWANCE_PREFIX}, which it mirrors in shape
 * but not in meaning:** an *allowance* grants a bonus USE of an action that has run out of
 * uses — it defeats the once-per-turn wall, but the action still costs its action points. An
 * *earmark* grants a free use — it zeroes the named verb's COST for one use, and does nothing
 * to the usage wall. The two are orthogonal, and a carrier may need both (RAW's "you may
 * Boost again as a free action" is a bonus use AND a zero cost); neither substitutes for the
 * other.
 *
 * **Turn-scoped**, reset for the CARRYING combatant at its own next `turn_start` by a prefix
 * sweep — an unused free Boost does not carry forward.
 */
export const FREE_ACTION_USE_PREFIX = "free_action_use:";

/**
 * The `current.resources` key carrying `verb`'s held free-use count (see
 * {@link FREE_ACTION_USE_PREFIX}). The one door onto the raw namespaced key.
 */
export function freeActionUseKey(verb: EarmarkVerb): string {
  return `${FREE_ACTION_USE_PREFIX}${verb}`;
}

/** How many free uses of `verb` does a combatant hold? Absent reads as 0 (no earmark). */
export function freeActionUses(resources: Record<string, number> | undefined, verb: EarmarkVerb): number {
  return resources?.[freeActionUseKey(verb)] ?? 0;
}

/**
 * A RESERVED `grant_action_allowance` `actionId` sentinel — **not** a real RAW-catalog verb
 * or granted composite id (it is never gated by `canUseAction`/`checkActionAvailable`, and no
 * catalog, frame-trait, or core-system id is ever this string).
 *
 * An overlay entity's OWN `action`-kind clause that grants ITSELF a bonus use under this
 * sentinel is the generic, content-authored signal `backend/src/encounterRoom.ts` reads to
 * fold a raw **+1 Quick action** onto the acting unit's `TurnBudget.actions` — the mechanism
 * behind an authored "…as a free action" (RAW). The room reacts generically to ANY
 * `request_activate`d entity whose resolved effect grants this sentinel to itself: a pure
 * content signal, with no entity-id enumeration anywhere.
 *
 * A grant naming a REAL catalog id (`{actionId: "skirmish"}`) does **not** trip this — only
 * this exact sentinel does — so such a grant keeps its narrower, usage-cap-only meaning: it
 * bypasses that action's once-per-turn/round/scene wall, but does not itself refund a point.
 */
export const GRANT_FREE_QUICK_ACTION_SENTINEL = "grant_quick_action";

/**
 * The combatant-resource key carrying a combatant's **OVERSHIELD** value (RAW Core p.105 —
 * "This system provides HP that disappears at the end of the scene or when a specified
 * condition is met. The user only retains the highest value of OVERSHIELD applied — it does
 * not stack. Damage is dealt to OVERSHIELD first, then HP. OVERSHIELD can push a character
 * past their maximum HP. It can't benefit from healing but otherwise benefits normally from
 * anything that would affect HP and damage").
 *
 * **Absent reads as 0** (no shield — unlike Loading's `?? 1`), so the ordinary
 * `resource_set`/`resource_delta` inverses round-trip without any absent-tracking machinery.
 * Grants are **keep-highest, computed at emit time**; the damage pipeline drains it via
 * effective `resource_delta`s before any `hp_delta`. Burn ticks ARE absorbed (burn is
 * damage); heat is NOT.
 *
 * **No expiry machinery**: RAW's "end of the scene" is encounter teardown (the state dies
 * with the encounter), and "when a specified condition is met" belongs to the granting
 * content's own duration machinery.
 */
export const OVERSHIELD_RESOURCE = "overshield";

/**
 * A combatant's global reaction-slot allotment (1 — RAW Core pp. 61, 73: at most one reaction per
 * turn, "your turn or other characters' turns"). The allotment is per turn window, reopened at
 * every turn start, not per round.
 */
export const REACTION_SLOT_ALLOWANCE = 1;

/**
 * Does combatant `id` currently have its {@link ActivationState.reactionSlot} open — may it
 * take a reaction (Overwatch or Brace) right now? A missing key reads as available. This is
 * the GLOBAL gate, independent of which named reaction is being spent; pair it with
 * {@link canUseReaction} for the named-reaction cap.
 */
export function reactionSlotOpen(activation: ActivationState, id: CombatantId): boolean {
  return (activation.reactionSlot[id] ?? REACTION_SLOT_ALLOWANCE) > 0;
}

/**
 * May a combatant still use the NAMED reaction `reactionId` (`"overwatch"` | `"brace"`) before
 * its own next turn, given ITS entry of {@link ActivationState.reactionUses}? The caller narrows to the
 * one combatant, exactly like {@link canUseAction} takes an already-narrowed `TurnBudget`, to
 * stay under the 3-parameter lint cap.
 *
 * Independent of {@link reactionSlotOpen}'s global slot: the entry is cleared only by the
 * combatant's OWN `activate` (Core p.73), so a unit gets one Overwatch between its own turns,
 * however many other turns reopen its slot.
 */
export function canUseReaction(
  uses: Record<string, number> | undefined,
  reactionId: string,
  allowance = 1,
): boolean {
  return (uses?.[reactionId] ?? 0) < allowance;
}

/**
 * May a combatant still use the ROUND-scoped action `actionId` this round, given ITS entry of
 * {@link ActivationState.actionRoundUses}? NOT reset by `activate` (only `endRound` resets
 * it) — so a multi-activation combatant still gets only its round allowance total, even
 * across multiple activations. Mirrors {@link canUseReaction}'s shape, one substrate over.
 */
export function canUseRoundAction(
  uses: Record<string, number> | undefined,
  actionId: string,
  allowance = 1,
): boolean {
  return (uses?.[actionId] ?? 0) < allowance;
}

/**
 * May a combatant still use the SCENE-scoped (`1/scene`) action `actionId` this encounter?
 * Reads the per-affordance ledger's `uses` count; an absent entry reads as 0 consumed (full).
 *
 * **The substrate is `current.affordanceState`, not a namespaced `current.resources` key**
 * (`decisions/0176`; `affordance-table` Phase 3.1 deleted the `scene_use:` prefix). Consumed-count
 * polarity is unchanged — absent = 0 consumed = full — and a scene count is still never seeded at
 * spawn or reset: **scene = encounter** in this engine, so there is no in-encounter reset to seed
 * against and a fresh encounter is a fresh ledger.
 *
 * **`actionId` is the ledger key**: the affordance's `key` for a scene-grain rate, or
 * `limited.pool ?? key` for a Limited charge pool (`decisions/0202`). The caller picks the slot for
 * the fact it is counting; this predicate takes whatever slot it is handed.
 *
 * Weapon `Limited {N}` rides the SAME table keyed by {@link weaponInstanceKey} instead of an
 * affordance key ({@link canUseLimitedWeapon}) — one flat namespace, since the two key schemes are
 * disjoint, with the same predicate shape.
 */
export function canUseSceneAction(
  affordanceState: CombatantCurrent["affordanceState"],
  actionId: string,
  allowance = 1,
): boolean {
  return (affordanceState?.[actionId]?.uses ?? 0) < allowance;
}

/**
 * May `weaponKey` still fire — has it consumed fewer than `max` of its **`Limited {N}`**
 * charges? Reads the SAME per-affordance ledger {@link canUseSceneAction} does, keyed by the
 * weapon's {@link weaponInstanceKey} (`decisions/0176` ruling 1 — weapons join the state ledger
 * from day one); absent reads as 0 consumed (full). The ONE shared read both the resolve-path gate
 * and the frontend chooser-greying predicate consult — never peek at the ledger by hand, exactly
 * like Loading's {@link isWeaponLoaded}.
 */
export function canUseLimitedWeapon(
  affordanceState: CombatantCurrent["affordanceState"],
  weaponKey: string,
  max = 1,
): boolean {
  return (affordanceState?.[weaponKey]?.uses ?? 0) < max;
}

/**
 * Every currently-consumed Limited-weapon charge count, keyed by {@link weaponInstanceKey} — the
 * commit extractor's read that populates `PrintedMechState.limitedUsed`, and the reason a Limited
 * weapon's spent charge clears on Full Repair rather than coming back free every encounter.
 * Returns COUNTS, not a presence list, because a Limited charge can be PARTIALLY consumed (2 of a
 * `Limited 3` weapon's uses spent) unlike a destroyed item's boolean. A zero-valued entry is
 * dropped, so a weapon that hasn't fired never round-trips a spurious `{key: 0}`.
 *
 * **It iterates the FROZEN weapon list, not the ledger.** The `limited:` prefix scan this replaced
 * used the key namespace itself as the filter; the ledger has no prefixes — a weapon instance key
 * and a granted action's affordance key live in one flat table — so the discriminator has to be
 * the frozen fact "this weapon declares `Limited {N}`", exactly as {@link limitedSystemUsedCounts}
 * discriminates on the frozen affordance's `limited` pool. That also narrows the result correctly: a
 * count left over for a weapon the mech no longer carries is not this mech's charge to persist.
 */
export function limitedUsedCounts(
  affordanceState: CombatantCurrent["affordanceState"],
  weapons: CombatantBase["weapons"],
): Record<string, number> {
  if (!affordanceState || !weapons) return {};
  const out: Record<string, number> = {};
  for (const w of weapons) {
    if (w.limited === undefined) continue;
    const used = affordanceState[weaponInstanceKey(w)]?.uses ?? 0;
    if (used > 0) out[weaponInstanceKey(w)] = used;
  }
  return out;
}

/**
 * The **Limited charge pool** a combatant holds for the first of `itemIds` it carries one for: its
 * ledger slot (`limited.pool ?? key`), its size and the charges left. Matches a frozen record by
 * its key or its granted item id. `undefined` when none of them has a frozen pool. Read by the
 * `limited_charges` predicate and the `expend_charges` atom (Walking Armory's Ammo Case).
 */
export function heldLimitedPool(c: { base: Pick<CombatantBase, "affordances">; current: Pick<CombatantCurrent, "affordanceState"> }, itemIds: readonly string[]): { slot: string; max: number; left: number } | undefined {
  for (const id of itemIds) {
    const a = (c.base.affordances ?? []).find((r) => r.limited !== undefined && (r.key === id || (r.source.kind === "granted" && r.source.itemId === id)));
    if (a?.limited === undefined) continue;
    const slot = a.limited.pool ?? a.key;
    return { slot, max: a.limited.max, left: Math.max(0, a.limited.max - (c.current.affordanceState?.[slot]?.uses ?? 0)) };
  }
  return undefined;
}

/**
 * Every currently-consumed **Limited-SYSTEM** charge count, keyed by ledger slot — the commit
 * extractor's read that populates `PrintedMechState.limitedSystemUsed`. The granted-action
 * sibling of {@link limitedUsedCounts}, and it takes the same shape: iterate the FROZEN table,
 * discriminate on the frozen fact, read the ledger by key.
 *
 * **It reads the `limited` field alone** (`decisions/0202`). The ledger also carries every ordinary
 * RAW "1/scene" ability's consumed count, a `cap` rate on its own `key`, and those must NEVER be
 * captured here: an ordinary ability resets for free every fresh encounter (scene = encounter), so
 * persisting its partial consumption would wrongly short it on the next redeploy. A record with no
 * `limited` pool is skipped, whatever its `cap` says.
 *
 * **The slot is `limited.pool ?? key`**, the same key `spend_action_use` writes (`budget.ts`'s
 * `ledgerKey`). Every shipped Limited system freezes a pooled record (the item-level `tg_limited`
 * arm always stamps `pool: itemId`, even for a single-affordance item), so the pool name, which
 * is nobody's affordance `key`, is what persists. Pooled siblings read and write the same slot
 * with the same value. `spawnMech` re-seeds each persisted key verbatim, which lands it back on
 * that slot.
 */
export function limitedSystemUsedCounts(
  affordanceState: CombatantCurrent["affordanceState"],
  affordances: CombatantBase["affordances"],
): Record<string, number> {
  if (!affordanceState || !affordances) return {};
  const out: Record<string, number> = {};
  for (const a of affordances) {
    if (a.limited === undefined) continue;
    const slot = a.limited.pool ?? a.key;
    const used = affordanceState[slot]?.uses ?? 0;
    if (used > 0) out[slot] = used;
  }
  return out;
}

/**
 * The `current.resources` key a frozen item's **destroyed** marker rides (Core p.80 System
 * Trauma) — namespaced by `key`, which is either a weapon's {@link weaponInstanceKey} or a
 * system's {@link FrozenSystem.instanceId}. The two schemes are disjoint, so one flat
 * namespace never collides. A namespaced key in the SAME sparse `current.resources` bag, not
 * a dedicated top-level field the way `discharge_set` has one.
 */
const DESTROYED_ITEM_PREFIX = "destroyed:";

export function destroyedItemResourceKey(key: string): string {
  return `${DESTROYED_ITEM_PREFIX}${key}`;
}

/**
 * Is the item named by `key` (a weapon's {@link weaponInstanceKey} or a system's
 * {@link FrozenSystem.instanceId}) currently **destroyed** on this combatant? Absent reads as
 * intact; present with any value `>= 1` means destroyed. The ONE shared read every
 * participation gate (`selectWeapon`, the Barrage fired-weapon ledger, `enumerateActions`)
 * consults — never peek at the key by hand.
 */
export function isItemDestroyed(resources: Record<string, number> | undefined, key: string): boolean {
  return (resources?.[destroyedItemResourceKey(key)] ?? 0) >= 1;
}

/**
 * Every destroyed item's **bare** key currently live on this combatant — strips the
 * {@link destroyedItemResourceKey} prefix off each `current.resources` key carrying a live
 * (`>= 1`) destroyed marker. Reuses the same prefix its two siblings share rather than
 * re-deriving the `"destroyed:"` literal, so the three can't drift apart. The commit
 * extractor uses this to populate `PrintedMechState.destroyedItems`.
 */
export function destroyedItemKeys(resources: Record<string, number> | undefined): string[] {
  if (!resources) return [];
  return Object.keys(resources)
    .filter((k) => k.startsWith(DESTROYED_ITEM_PREFIX) && (resources[k] ?? 0) >= 1)
    .map((k) => k.slice(DESTROYED_ITEM_PREFIX.length));
}
